/**
 * Quick "View as user" picker — opens from the AdminLayout user dropdown so
 * super-admins can switch identity without navigating to /users or /hris.
 *
 * Server-side search via /v4/employees (debounced). Each row exposes the
 * shared ImpersonateUserButton which owns the reason modal + mutation.
 */

import { useEffect, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import { useEmployees } from '@/features/teams/api/employees';
import { useSession } from '@/hooks/useSession';
import { ImpersonateUserButton } from './ImpersonateUserButton';

interface ImpersonateUserPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImpersonateUserPicker({ open, onOpenChange }: ImpersonateUserPickerProps) {
  const { user } = useSession();
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 250);
  const { data, isLoading } = useEmployees({ search: debounced || undefined });

  // Reset search when reopening so the previous query doesn't leak between sessions.
  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  // PG-only: list employees by their PG UUID (`emp.id`) and exclude self.
  // V4 session `user._id` is the caller's pg_id, so the equality check is a
  // direct PG UUID comparison.
  const employees = (data?.employees ?? [])
    .filter((emp) => emp.id && emp.id !== user?._id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>View platform as another user</DialogTitle>
          <DialogDescription>
            Pick the user whose view you want to adopt. You'll be in their session for up to
            60 minutes; every action will be attributed to them in audit logs, with your
            identity recorded as the impersonator.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search by name, email, position…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="max-h-80 overflow-y-auto -mx-6 px-6 divide-y">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              No employees match this search.
            </div>
          ) : (
            employees.map((emp) => {
              const fullName = [emp.firstname, emp.lastname].filter(Boolean).join(' ')
                || emp.email
                || '—';
              return (
                <div key={emp.id} className="flex items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{fullName}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {emp.email}
                      {emp.position && <span> · {emp.position}</span>}
                    </div>
                  </div>
                  <ImpersonateUserButton
                    targetUserId={emp.id as string}
                    targetLabel={fullName}
                    size="sm"
                  />
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ImpersonateUserPicker;
